<script setup lang="ts">
import {
    Button,
    Card,
    CardContent,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    ZhSectionHeading,
    ZhStatusPill,
} from '@zerohuman/ui'
import { computed, onMounted, ref } from 'vue'
import { RouterLink } from 'vue-router'
import { api, type ExpressTool, refusalCode, type ToolBindingRow, type ToolLayer, type ToolNeed } from '@/api'
import ListSearchField from '@/components/ListSearchField.vue'
import { oauthStartRefusalMessage } from '@/components/oauth-start-refusals'
import { bindRefusalMessage } from '@/components/tool-binding-refusals'
import { categoriesOf, directoryOf, exampleTaskChat, expressDocsPage, SHORT_DIRECTORY } from '@/lib/expressDirectory'
import { commonToolsFirst, isCommonTool } from '@/lib/moreTools'

/**
 * The Express tools: systems the platform has set up, so that connecting one is pressing Connect and signing in.
 * Nothing about the server, its permissions or its sign-in app is asked of the person, because the tool's own
 * record holds all of it.
 *
 * Where the page is already about one layer (a member's or a team's Tools), Connect goes straight to the sign-in
 * for that layer. On the Tools page it first asks who the connection is for, since a personal account connected for
 * the whole enterprise would be every member's to use.
 *
 * A long directory opens on its featured tools and the ones already connected, with a search and a category filter
 * over the whole list (lib/expressDirectory). A short one is simply shown.
 *
 * The optional onboarding tools step (#1942) reuses it through opt-in props, all off on the Tools page:
 * - `returnTo` brings the sign-in back to the step instead of the Tools page;
 * - `readOnly` puts "Ask your account owner to connect tools" in place of every Connect button;
 * - `stepMode` adds a waiting state while the directory loads, a plain error with Retry when it cannot, "Connecting…"
 *   on the pressed button, and the common tools (GitHub, Google, Slack, Notion, Linear) first;
 * - `needs` lists the tool types the team needs with their status and no Connect button, and leaves them out of the
 *   directory, so none is asked for twice.
 */
type Target = { id: string; label: string }

const props = defineProps<{
    /** The layer the page is about. Left out on the Tools page, where the person chooses. */
    lockLayer?: ToolLayer
    lockLayerId?: string
    enterpriseId: string
    teams: Target[]
    members: Target[]
    /** The connections this page lists, to mark a tool that is already connected. */
    rows: ToolBindingRow[]
    /** Onboarding step (#1942), all off by default so the Tools page is unchanged. Where a sign-in returns to: an allow-listed path. */
    returnTo?: string
    /** Shows a sentence in place of every Connect button. */
    readOnly?: boolean
    /** Waiting states, a load error with Retry, "Connecting…" and the common tools first. */
    stepMode?: boolean
    /** The tool types the team needs, shown with their status and no Connect button. */
    needs?: ToolNeed[]
}>()

const emit = defineEmits<{
    /** A sign-in is being started for `tool`, just before the browser leaves for it. */
    connecting: [tool: ExpressTool]
}>()

/** The layers a person may choose between: who uses the connection. Roles and tasks stay on the full form. */
const LAYERS: Array<{ value: ToolLayer; label: string }> = [
    { value: 'member', label: 'One member' },
    { value: 'team', label: 'A team' },
    { value: 'enterprise', label: 'The whole enterprise' },
]

const tools = ref<ExpressTool[]>([])
const error = ref<string | null>(null)
const busy = ref(false)
/** The directory is being read; on the step, a waiting state shows meanwhile. */
const loading = ref(true)
/** The directory could not be read; on the step, a plain error with Retry shows. */
const loadFailed = ref(false)
/** The tool whose sign-in is being started. */
const connecting = ref<string | null>(null)
/** The tool whose "who uses this?" question is open, on the Tools page only. */
const asking = ref<ExpressTool | null>(null)
const layer = ref<ToolLayer>('member')
const layerId = ref('')

const targets = computed<Target[]>(() => {
    if (layer.value === 'enterprise') {
        return [{ id: props.enterpriseId, label: 'Everyone' }]
    }
    return layer.value === 'team' ? props.teams : props.members
})

const connectedIds = computed(() => new Set(props.rows.map(row => row.expressToolId).filter(Boolean)))
const connectedTypes = computed(() => new Set(props.rows.map(row => row.toolType)))

/**
 * Whether the person already has the tool, through it or by hand under the same tool type. It keeps the tool in view
 * on a long directory; only a connection made through the tool is called Connected.
 */
function inUse(tool: ExpressTool): boolean {
    return connectedIds.value.has(tool.id) || connectedTypes.value.has(tool.slug)
}

/** The tool types listed as the team's needs: shown with their status, and never offered again in the directory. */
const needTypes = computed(() => new Set((props.needs ?? []).map(need => need.connect)))
/** The tools the directory offers: every one but the team's needs, with the common ones first on the step. */
const offered = computed(() => {
    const rest = tools.value.filter(tool => !needTypes.value.has(tool.slug))
    return props.stepMode ? commonToolsFirst(rest) : rest
})

const query = ref('')
/** The category chosen, or `ALL` for every one: the kit select has no empty value. */
const ALL = 'all'
const category = ref(ALL)
const showAll = ref(false)
const long = computed(() => offered.value.length > SHORT_DIRECTORY)
const categories = computed(() => categoriesOf(offered.value))
const directory = computed(() =>
    directoryOf(
        offered.value,
        { query: query.value, category: category.value === ALL ? '' : category.value, showAll: showAll.value },
        inUse,
        props.stepMode ? isCommonTool : undefined,
    ),
)

/** What a tool's button says: "Connecting…" on the step while its sign-in starts, else Connect or Reconnect. */
function connectLabel(tool: ExpressTool): string {
    if (props.stepMode && connecting.value === tool.id) {
        return 'Connecting…'
    }
    return connectedIds.value.has(tool.id) ? 'Reconnect' : 'Connect'
}

/** A tool's own docs page where its record names one, else its page on the public site, which only a live tool has. */
function docsOf(tool: ExpressTool): string | null {
    return tool.docsUrl || tool.live ? expressDocsPage(tool) : null
}

function pickLayer(next: ToolLayer): void {
    layer.value = next
    layerId.value = targets.value[0]?.id ?? ''
}

/** Only an address the browser can leave for: the sign-in is always on another site, over https. */
function leave(url: string): void {
    window.location.assign(url)
}

/** Why a sign-in could not be started, when the reason is not one the Tools page already has words for. */
function startFailure(tool: ExpressTool, failure: unknown): string {
    if (refusalCode(failure) === 'member_scope') {
        return `You can see tools here but not connect them. Ask the owner of this enterprise to let you change tools, then connect ${tool.name}.`
    }
    return `Could not start connecting ${tool.name}. Try again in a moment.`
}

async function start(tool: ExpressTool, at: { layer: ToolLayer; layerId: string }): Promise<void> {
    busy.value = true
    connecting.value = tool.id
    error.value = null
    emit('connecting', tool)
    try {
        const started = await api.connectExpressTool(
            tool.slug,
            props.returnTo ? { ...at, returnTo: props.returnTo } : at,
        )
        leave(started.authorizationUrl)
    } catch (failure) {
        // #1936: a tool's server that refused or could not be reached is named in the Tools page's words, with a
        // next step that fits a tool whose address and sign-in app the person cannot change.
        error.value =
            oauthStartRefusalMessage(failure, tool.name, { express: true }) ??
            bindRefusalMessage(failure) ??
            startFailure(tool, failure)
    } finally {
        busy.value = false
        connecting.value = null
    }
}

/** Where the question starts: a member where there is one, since a personal account is one member's to use. */
function firstLayer(): ToolLayer {
    if (props.members.length) {
        return 'member'
    }
    return props.teams.length ? 'team' : 'enterprise'
}

async function connect(tool: ExpressTool): Promise<void> {
    if (props.lockLayer) {
        await start(tool, {
            layer: props.lockLayer,
            layerId: props.lockLayerId || (props.lockLayer === 'enterprise' ? props.enterpriseId : ''),
        })
        return
    }
    pickLayer(firstLayer())
    asking.value = tool
}

async function confirm(): Promise<void> {
    const tool = asking.value
    if (!tool || !layerId.value) {
        return
    }
    asking.value = null
    await start(tool, { layer: layer.value, layerId: layerId.value })
}

/** Reads the directory, again on Retry. */
async function load(): Promise<void> {
    loading.value = true
    loadFailed.value = false
    try {
        tools.value = await api.expressTools()
    } catch {
        // The directory is an offer, never a requirement: without it the page is the Tools page it always was. On
        // the step, it says so and offers Retry.
        tools.value = []
        loadFailed.value = true
    } finally {
        loading.value = false
    }
}

onMounted(load)
</script>

<template>
	<section v-if="stepMode || tools.length" class="space-y-3" data-testid="express-directory">
		<header class="space-y-1">
			<ZhSectionHeading as="h3">Express</ZhSectionHeading>
			<p v-if="readOnly" class="text-muted-foreground text-sm" data-testid="express-read-only">
				Ask your account owner to connect tools. You can still see what is available here.
			</p>
			<p v-else class="text-muted-foreground text-sm">
				Ready to connect: press Connect and sign in. There is no address, key or app to set up.
			</p>
		</header>
		<div v-if="needs?.length" class="space-y-2" data-testid="express-needs">
			<p class="text-sm font-medium">Tools your team needs</p>
			<p class="text-muted-foreground text-sm">Team setup asks for these, so they are not offered again below.</p>
			<div class="flex flex-wrap gap-3">
				<div
					v-for="need in needs"
					:key="need.connect"
					class="flex items-center gap-2 text-sm"
					data-testid="express-need"
					:data-connect="need.connect"
				>
					<span class="font-medium">{{ need.name }}</span>
					<ZhStatusPill :tone="need.status === 'connected' ? 'completed' : 'neutral'" :pulse="false">
						{{ need.status === 'connected' ? 'Connected' : 'Not connected' }}
					</ZhStatusPill>
				</div>
			</div>
		</div>
		<p v-if="stepMode && loading" class="text-muted-foreground text-sm" role="status" data-testid="express-loading">
			Loading the tool directory…
		</p>
		<div v-else-if="stepMode && loadFailed" class="flex flex-wrap items-center gap-2">
			<p class="text-destructive text-sm" data-testid="express-load-error">
				The tool directory could not load. Check your connection and try again.
			</p>
			<Button type="button" size="sm" variant="outline" data-testid="express-retry" @click="load">Retry</Button>
		</div>
		<p v-if="error" class="text-destructive text-sm" role="alert" data-testid="express-error">{{ error }}</p>
		<div v-if="long" class="flex flex-wrap items-center gap-2" data-testid="express-filters">
			<ListSearchField
				v-model="query"
				type="search"
				class="w-full sm:w-64"
				placeholder="Search tools"
				aria-label="Search Express tools"
				data-testid="express-search"
			/>
			<Select v-if="categories.length > 1" v-model="category">
				<SelectTrigger class="w-full sm:w-56" aria-label="Category" data-testid="express-category">
					<SelectValue />
				</SelectTrigger>
				<SelectContent>
					<SelectItem :value="ALL">All categories</SelectItem>
					<SelectItem v-for="name in categories" :key="name" :value="name">{{ name }}</SelectItem>
				</SelectContent>
			</Select>
		</div>
		<p v-if="offered.length && !directory.shown.length" class="text-muted-foreground text-sm" data-testid="express-none">
			No Express tool matches that. Any system with an MCP server can still be connected with Add Tool.
		</p>
		<div class="grid gap-3 sm:grid-cols-2">
			<Card v-for="tool in directory.shown" :key="tool.id" data-testid="express-tool" :data-slug="tool.slug">
				<CardContent class="flex items-start justify-between gap-3">
					<div class="flex min-w-0 items-start gap-3">
						<img v-if="tool.imageUrl" :src="tool.imageUrl" alt="" class="size-8 shrink-0 object-contain" />
						<div class="min-w-0 space-y-1">
							<div class="flex flex-wrap items-center gap-2">
								<p class="font-medium">{{ tool.name }}</p>
								<ZhStatusPill tone="system" :pulse="false">Express</ZhStatusPill>
								<ZhStatusPill v-if="!tool.live" tone="neutral" :pulse="false">Draft</ZhStatusPill>
								<ZhStatusPill
									v-if="connectedIds.has(tool.id)"
									tone="completed"
									:pulse="false"
									data-testid="express-connected"
								>
									Connected
								</ZhStatusPill>
							</div>
							<p class="text-muted-foreground text-sm">{{ tool.description }}</p>
							<a
								v-if="docsOf(tool)"
								:href="docsOf(tool) ?? undefined"
								target="_blank"
								rel="noopener"
								class="text-sm underline underline-offset-4"
								data-testid="express-docs"
							>
								How it works
							</a>
							<!--
								A first task to make with the tool, once the person has it: a create-a-task conversation opens
								with the request written out, and nothing is sent until they send it.
							-->
							<RouterLink
								v-if="tool.exampleTask && inUse(tool)"
								:to="exampleTaskChat(tool.slug)"
								class="block text-sm underline underline-offset-4"
								data-testid="express-example-task"
							>
								Try an example task
							</RouterLink>
						</div>
					</div>
					<Button
						v-if="!readOnly"
						type="button"
						size="sm"
						class="shrink-0"
						:disabled="busy"
						@click="connect(tool)"
					>
						{{ connectLabel(tool) }}
					</Button>
				</CardContent>
			</Card>
		</div>
		<Button
			v-if="directory.more > 0"
			type="button"
			variant="outline"
			size="sm"
			data-testid="express-show-all"
			@click="showAll = true"
		>
			Show all {{ offered.length }} tools
		</Button>
		<Dialog :open="asking !== null" @update:open="open => { if (!open) asking = null }">
			<DialogContent class="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Who uses {{ asking?.name }}?</DialogTitle>
					<DialogDescription>
						You sign in next. Whoever you choose here acts as the account you sign in with.
					</DialogDescription>
				</DialogHeader>
				<div class="space-y-3">
					<label class="block text-sm">
						<span class="text-muted-foreground">Connect for</span>
						<Select :model-value="layer" @update:model-value="value => pickLayer(value as ToolLayer)">
							<SelectTrigger class="mt-1 w-full" data-testid="express-layer"><SelectValue /></SelectTrigger>
							<SelectContent>
								<SelectItem v-for="option in LAYERS" :key="option.value" :value="option.value">
									{{ option.label }}
								</SelectItem>
							</SelectContent>
						</Select>
					</label>
					<label v-if="layer !== 'enterprise'" class="block text-sm">
						<span class="text-muted-foreground">{{ layer === 'team' ? 'Team' : 'Member' }}</span>
						<Select v-model="layerId">
							<SelectTrigger class="mt-1 w-full" data-testid="express-target"><SelectValue /></SelectTrigger>
							<SelectContent>
								<SelectItem v-for="target in targets" :key="target.id" :value="target.id">
									{{ target.label }}
								</SelectItem>
							</SelectContent>
						</Select>
					</label>
					<p v-if="layer === 'enterprise'" class="text-muted-foreground text-sm">
						Every member can then use this account. Only the owner, or a member the owner has allowed, can
						connect a tool for the whole enterprise.
					</p>
				</div>
				<DialogFooter>
					<Button type="button" variant="outline" @click="asking = null">Cancel</Button>
					<Button type="button" :disabled="!layerId || busy" data-testid="express-continue" @click="confirm">
						Continue to sign in
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	</section>
</template>
